import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface StatusBarItem {
  label: string;
  severity: "critical" | "warning" | "info";
}

interface Props {
  onClick: () => void;
}

export default function PaletteTrigger({ onClick }: Props) {
  const [statusIndex, setStatusIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const { data } = useQuery<{ statusBar: StatusBarItem[] }>({
    queryKey: ["/api/palette/context"],
    staleTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
  });

  const statusItems = data?.statusBar || [];

  // Rotate status items every 4 seconds with fade
  useEffect(() => {
    if (statusItems.length <= 1) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setStatusIndex((prev) => (prev + 1) % statusItems.length);
        setVisible(true);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [statusItems.length]);

  const currentStatus = statusItems[statusIndex % Math.max(statusItems.length, 1)];

  const severityColor: Record<string, string> = {
    critical: "text-red-400",
    warning: "text-amber-400",
    info: "text-white/40",
  };

  return (
    <button
      type="button"
      aria-label={currentStatus ? `Search — ${currentStatus.label}` : "Search, navigate, or ask"}
      onClick={onClick}
      className="flex items-center gap-2 w-full md:w-auto min-h-[40px] md:min-h-0 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] cursor-pointer hover:bg-white/[0.06] hover:border-[#B8860B]/20 transition-all group min-w-0 max-w-full"
    >
      <Search className="w-4 h-4 text-white/30 flex-shrink-0" />
      <span
        className={`text-xs transition-opacity duration-300 flex-1 md:flex-none min-w-0 sm:min-w-[120px] text-left truncate ${
          visible ? "opacity-100" : "opacity-0"
        } ${currentStatus ? severityColor[currentStatus.severity] || "text-white/40" : "text-white/40"}`}
      >
        {currentStatus ? currentStatus.label : "Search, navigate, or ask..."}
      </span>
      <kbd className="text-[10px] text-white/30 bg-white/5 px-1 py-0.5 rounded border border-white/10 flex-shrink-0 hidden sm:inline">
        ⌘K
      </kbd>
    </button>
  );
}
